import React from "react";
import {
  X, Check, CheckCircle, Loader2, ChevronLeft, ChevronRight,
  Info, MapPin, Image, ListChecks, IndianRupee, Package, FileText, Landmark, ScrollText,
} from "lucide-react";

// Label + icon for every step the service forms can have
const STEP_META = {
  basic:     { label: "Basics",    icon: Info },
  location:  { label: "Location",  icon: MapPin },
  media:     { label: "Photos",    icon: Image },
  details:   { label: "Details",   icon: ListChecks },
  pricing:   { label: "Pricing",   icon: IndianRupee },
  packages:  { label: "Packages",  icon: Package },
  documents: { label: "Documents", icon: FileText },
  payout:    { label: "Payout",    icon: Landmark },
  policies:  { label: "Policies",  icon: ScrollText },
};

const stepMeta = (key) => STEP_META[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), icon: Info };

/**
 * ServiceFormHeader — title bar + step navigation shared by the add / edit
 * service forms (venue, photography, catering, decor).
 */
export const ServiceFormHeader = ({ icon: Icon, title, subtitle, tabs, activeTab, onTabChange, onClose, inline }) => {
  const activeIdx = Math.max(0, tabs.indexOf(activeTab));

  return (
    <>
      {/* Title bar */}
      <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-br from-purple-50 via-white to-pink-50 px-5 sm:px-7 py-5">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-['Manrope'] text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 truncate">{title}</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {subtitle ? `${subtitle} · ` : ""}Step {activeIdx + 1} of {tabs.length} — {stepMeta(activeTab).label}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Progress */}
        <div className="relative mt-4 h-1.5 rounded-full bg-white/80 ring-1 ring-inset ring-gray-200/70 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500"
            style={{ width: `${((activeIdx + 1) / tabs.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Steps — stay pinned under the dashboard top bar while the form scrolls */}
      <div className={`sticky ${inline ? "top-[4.5rem]" : "top-0"} z-10 flex items-center gap-1.5 overflow-x-auto border-b border-gray-200/70 bg-white/95 backdrop-blur-md px-4 sm:px-6 py-3`}>
        {tabs.map((tab, i) => {
          const meta = stepMeta(tab);
          const isActive = i === activeIdx;
          const isDone = i < activeIdx;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => onTabChange(tab)}
              aria-current={isActive ? "step" : undefined}
              className={`group flex flex-shrink-0 items-center gap-2 rounded-xl py-1.5 pl-1.5 pr-3.5 text-sm font-semibold whitespace-nowrap transition-all duration-150 ${
                isActive
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold transition-colors ${
                  isActive
                    ? "bg-white/20 text-white"
                    : isDone
                    ? "bg-purple-50 text-purple-600 ring-1 ring-inset ring-purple-200/70"
                    : "bg-gray-100 text-gray-500 ring-1 ring-inset ring-gray-200/60"
                }`}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : <meta.icon className="h-3.5 w-3.5" />}
              </span>
              {meta.label}
            </button>
          );
        })}
      </div>
    </>
  );
};

const FooterSwitch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-2.5"
  >
    <span className={`relative h-[22px] w-10 flex-shrink-0 rounded-full transition-colors duration-200 ${checked ? "bg-gradient-to-r from-purple-600 to-pink-600" : "bg-gray-300"}`}>
      <span className={`absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-[18px]" : "translate-x-0"}`} />
    </span>
    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{label}</span>
  </button>
);

/**
 * ServiceFormFooter — live / GST switches, step navigation and save.
 */
export const ServiceFormFooter = ({
  tabs, activeTab, onTabChange,
  isLive, onLiveChange, isGst, onGstChange,
  onClose, onSave, loading, isEdit,
}) => {
  const activeIdx = Math.max(0, tabs.indexOf(activeTab));
  const isFirst = activeIdx === 0;
  const isLast = activeIdx === tabs.length - 1;

  return (
    <div className="sticky bottom-0 z-10 rounded-b-2xl border-t border-gray-200/70 bg-white/95 backdrop-blur-md px-5 sm:px-7 py-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <FooterSwitch checked={!!isLive} onChange={onLiveChange} label="Service is Live" />
          <FooterSwitch checked={!!isGst} onChange={onGstChange} label="Inclusive of GST" />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-4 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isFirst}
            onClick={() => onTabChange(tabs[activeIdx - 1])}
            className="inline-flex h-11 items-center gap-1.5 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={() => onTabChange(tabs[activeIdx + 1])}
              className="inline-flex h-11 items-center gap-1.5 px-4 rounded-xl border border-purple-200 bg-purple-50 text-sm font-semibold text-gray-900 hover:bg-purple-100 transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 px-5 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg disabled:opacity-50 transition-all"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            {loading ? "Saving..." : isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};
